export enum TaskView {
  LOCAL = 'local',
  GLOBAL = 'global',
  DEPLOYED = 'deployed',
  EVENT = 'event',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SPAWNED = 'spawned',
  GLOBAL_LINKED = 'global-linked',
  LOCAL_LINKED = 'local-linked',
}

export enum TaskViewTab {
  EDIT_TASK = 0,
  WORK_ON_TASK = 1,
  TASK_INFO = 2,
  COMMENTS = 3,
  SETTINGS = 4,
  SCORE = 5,
}

export enum EditTaskAttributes {
  NAME = 1,
  ITEMS = 2,
  SELECT_SET = 3,
  SELECT_CATEGORY = 4,
  DEFAULT_ASSIGNEE = 5,
  NAME_ADDITION = 6,
}

export enum WorkOnTaskAttributes {
  PROGRESS = 0,
  ITEMS_EDITABLE = 1,
  ITEMS_READONLY = 2,
  RESET = 3,
  TASK_FAMILY = 4,
  DOCUMENTS = 5,
  DELAY_TASK = 6,
  REASSIGN_TASK = 7,
  COMPLETE_TASK = 8,
  TASK_ASSIGNABLE = 9,
  TASK_REASSIGNABLE = 10,
}

export enum TaskInfoAttributes {
  NAME = 'name',
  CREATED_BY = 'created-by',
  CREATED_ON = 'created-on',
  DEPLOYED_ON = 'deployed-on',
  COMPLETED_ON = 'completed-on',
  FAILED_ON = 'failed-on',
  SPAWNED_ON = 'spawned-on',
  TASK_ID = 'task-id',
  CATEGORY = 'category',
  LIFECYCLE = 'lifecycle',
  SUMMARY_READONLY = 'summary-readonly',
  SUMMARY_EDITABLE = 'summary-editable',
  BUILDER_PUBLIC_NOTES_READONLY = 'public-notes-readonly',
  BUILDER_PUBLIC_NOTES_EDITABLE = 'public-notes-editable',
  BUILDER_PRIVATE_NOTES_EDITABLE = 'private-notes-editable',
}

export enum CommentsAttributes {
  COMMENTS_TABLE = 0,
  ADD_COMMENT = 1,
  ACTIVITIES = 2,
}

export enum SettingsAttributes {
  DEPLOY = 0,
  OPEN = 1,
  DUPLICATE = 2,
  DELETE = 3,
  SCHEDULES = 4,
  SCHEDULED_INSTANCES = 5,
  TOGGLE_SCHEDULE = 6,
  ATTRIBUTES = 7,
  ATTRIBUTES_READONLY = 8,
  COLOR_EDITABLE = 9,
  COLOR_READONLY = 10,
  NOTIFICATIONS = 11,
  DOCUMENTS = 12,
  DEACTIVATE = 13,
  UNLINK = 14,
}

export class TaskViewHelper {
  private static TaskViewTabMap = new Map<TaskView, TaskViewTab[]>([
    [
      TaskView.LOCAL,
      [
        TaskViewTab.EDIT_TASK,
        TaskViewTab.WORK_ON_TASK,
        TaskViewTab.TASK_INFO,
        TaskViewTab.SETTINGS,
      ],
    ],
    [
      TaskView.GLOBAL,
      [
        TaskViewTab.EDIT_TASK,
        TaskViewTab.WORK_ON_TASK,
        TaskViewTab.TASK_INFO,
        TaskViewTab.SETTINGS,
      ],
    ],
    [
      TaskView.LOCAL_LINKED,
      [
        TaskViewTab.EDIT_TASK,
        TaskViewTab.WORK_ON_TASK,
        TaskViewTab.TASK_INFO,
        TaskViewTab.SETTINGS,
      ],
    ],
    [
      TaskView.GLOBAL_LINKED,
      [
        TaskViewTab.EDIT_TASK,
        TaskViewTab.WORK_ON_TASK,
        TaskViewTab.TASK_INFO,
        TaskViewTab.SETTINGS,
      ],
    ],
    [TaskView.DEPLOYED, [TaskViewTab.WORK_ON_TASK, TaskViewTab.TASK_INFO, TaskViewTab.SETTINGS]],
    [TaskView.EVENT, [TaskViewTab.WORK_ON_TASK, TaskViewTab.TASK_INFO]],
    [
      TaskView.COMPLETED,
      [TaskViewTab.WORK_ON_TASK, TaskViewTab.TASK_INFO, TaskViewTab.COMMENTS, TaskViewTab.SCORE],
    ],
    [
      TaskView.FAILED,
      [TaskViewTab.WORK_ON_TASK, TaskViewTab.TASK_INFO, TaskViewTab.COMMENTS, TaskViewTab.SCORE],
    ],
    [TaskView.SPAWNED, [TaskViewTab.WORK_ON_TASK, TaskViewTab.TASK_INFO, TaskViewTab.COMMENTS]],
  ]);

  private static editTaskMap = new Map<TaskView, EditTaskAttributes[]>([
    [
      TaskView.LOCAL,
      [
        EditTaskAttributes.NAME,
        EditTaskAttributes.ITEMS,
        EditTaskAttributes.SELECT_SET,
        EditTaskAttributes.SELECT_CATEGORY,
        EditTaskAttributes.DEFAULT_ASSIGNEE,
        EditTaskAttributes.NAME_ADDITION,
      ],
    ],
    [
      TaskView.GLOBAL,
      [
        EditTaskAttributes.NAME,
        EditTaskAttributes.ITEMS,
        EditTaskAttributes.SELECT_SET,
        EditTaskAttributes.SELECT_CATEGORY,
        EditTaskAttributes.DEFAULT_ASSIGNEE,
        EditTaskAttributes.NAME_ADDITION,
      ],
    ],
    [TaskView.DEPLOYED, []],
    [TaskView.EVENT, []],
    [TaskView.COMPLETED, []],
    [TaskView.FAILED, []],
    [TaskView.SPAWNED, []],
  ]);

  private static workOnTaskMap = new Map<TaskView, WorkOnTaskAttributes[]>([
    [
      TaskView.LOCAL,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.GLOBAL,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.LOCAL_LINKED,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.GLOBAL_LINKED,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.DEPLOYED,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
        WorkOnTaskAttributes.TASK_ASSIGNABLE,
        WorkOnTaskAttributes.TASK_REASSIGNABLE,
      ],
    ],
    [TaskView.EVENT, [WorkOnTaskAttributes.ITEMS_READONLY, WorkOnTaskAttributes.TASK_FAMILY]],
    [
      TaskView.COMPLETED,
      [
        WorkOnTaskAttributes.ITEMS_READONLY,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.FAILED,
      [
        WorkOnTaskAttributes.ITEMS_READONLY,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
      ],
    ],
    [
      TaskView.SPAWNED,
      [
        WorkOnTaskAttributes.PROGRESS,
        WorkOnTaskAttributes.ITEMS_EDITABLE,
        WorkOnTaskAttributes.RESET,
        WorkOnTaskAttributes.TASK_FAMILY,
        WorkOnTaskAttributes.DOCUMENTS,
        WorkOnTaskAttributes.DELAY_TASK,
        WorkOnTaskAttributes.REASSIGN_TASK,
        WorkOnTaskAttributes.COMPLETE_TASK,
      ],
    ],
  ]);

  private static taskInfoMap = new Map<TaskView, TaskInfoAttributes[]>([
    [
      TaskView.LOCAL,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.CREATED_BY,
        TaskInfoAttributes.CREATED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_EDITABLE,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_EDITABLE,
        TaskInfoAttributes.BUILDER_PRIVATE_NOTES_EDITABLE,
      ],
    ],
    [
      TaskView.GLOBAL,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.CREATED_BY,
        TaskInfoAttributes.CREATED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_EDITABLE,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_EDITABLE,
        TaskInfoAttributes.BUILDER_PRIVATE_NOTES_EDITABLE,
      ],
    ],
    [
      TaskView.DEPLOYED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.DEPLOYED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.EVENT,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.DEPLOYED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.COMPLETED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.COMPLETED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.FAILED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.FAILED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.SPAWNED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.SPAWNED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.LOCAL_LINKED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.DEPLOYED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_READONLY,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_READONLY,
      ],
    ],
    [
      TaskView.GLOBAL_LINKED,
      [
        TaskInfoAttributes.NAME,
        TaskInfoAttributes.CREATED_BY,
        TaskInfoAttributes.CREATED_ON,
        TaskInfoAttributes.TASK_ID,
        TaskInfoAttributes.CATEGORY,
        TaskInfoAttributes.LIFECYCLE,
        TaskInfoAttributes.SUMMARY_EDITABLE,
        TaskInfoAttributes.BUILDER_PUBLIC_NOTES_EDITABLE,
        TaskInfoAttributes.BUILDER_PRIVATE_NOTES_EDITABLE,
      ],
    ],
  ]);

  private static commentsMap = new Map<TaskView, CommentsAttributes[]>([
    [TaskView.LOCAL, []],
    [TaskView.GLOBAL, []],
    [TaskView.DEPLOYED, []],
    [TaskView.EVENT, []],
    [TaskView.COMPLETED, [CommentsAttributes.COMMENTS_TABLE, CommentsAttributes.ADD_COMMENT]],
    [TaskView.FAILED, [CommentsAttributes.COMMENTS_TABLE, CommentsAttributes.ADD_COMMENT]],
    [
      TaskView.SPAWNED,
      [
        CommentsAttributes.COMMENTS_TABLE,
        CommentsAttributes.ADD_COMMENT,
        CommentsAttributes.ACTIVITIES,
      ],
    ],
  ]);

  private static settingsMap = new Map<TaskView, SettingsAttributes[]>([
    [
      TaskView.LOCAL,
      [
        SettingsAttributes.OPEN,
        SettingsAttributes.DUPLICATE,
        SettingsAttributes.DELETE,
        SettingsAttributes.SCHEDULES,
        SettingsAttributes.COLOR_EDITABLE,
        SettingsAttributes.NOTIFICATIONS,
        SettingsAttributes.DOCUMENTS,
        SettingsAttributes.ATTRIBUTES,
        SettingsAttributes.SCHEDULED_INSTANCES,
        SettingsAttributes.TOGGLE_SCHEDULE,
      ],
    ],
    [
      TaskView.GLOBAL,
      [
        SettingsAttributes.DEPLOY,
        SettingsAttributes.DUPLICATE,
        SettingsAttributes.DELETE,
        SettingsAttributes.SCHEDULES,
        SettingsAttributes.COLOR_EDITABLE,
        SettingsAttributes.NOTIFICATIONS,
        SettingsAttributes.DOCUMENTS,
        SettingsAttributes.ATTRIBUTES,
      ],
    ],
    [
      TaskView.LOCAL_LINKED,
      [SettingsAttributes.DOCUMENTS, SettingsAttributes.DEACTIVATE, SettingsAttributes.UNLINK],
    ],
    [
      TaskView.GLOBAL_LINKED,
      [
        SettingsAttributes.DUPLICATE,
        SettingsAttributes.DOCUMENTS,
        SettingsAttributes.DEACTIVATE,
        SettingsAttributes.UNLINK,
      ],
    ],
    [
      TaskView.DEPLOYED,
      [
        SettingsAttributes.OPEN,
        SettingsAttributes.DELETE,
        SettingsAttributes.SCHEDULES,
        SettingsAttributes.DUPLICATE,
        SettingsAttributes.COLOR_READONLY,
        SettingsAttributes.NOTIFICATIONS,
        SettingsAttributes.TOGGLE_SCHEDULE,
        SettingsAttributes.SCHEDULED_INSTANCES,
        SettingsAttributes.ATTRIBUTES_READONLY,
      ],
    ],
    [TaskView.EVENT, []],
    [TaskView.COMPLETED, []],
    [TaskView.FAILED, []],
    [TaskView.SPAWNED, []],
  ]);

  static getTaskViewTabArray(taskView: TaskView): TaskViewTab[] {
    return this.TaskViewTabMap.get(taskView) ?? [];
  }

  static getEditTaskArray(taskView: TaskView): EditTaskAttributes[] {
    return this.editTaskMap.get(taskView) ?? [];
  }

  static getWorkOnTaskArray(taskView: TaskView): WorkOnTaskAttributes[] {
    return this.workOnTaskMap.get(taskView) ?? [];
  }

  static getTaskInfoArray(taskView: TaskView): TaskInfoAttributes[] {
    return this.taskInfoMap.get(taskView) ?? [];
  }

  static getCommentsArray(taskView: TaskView): CommentsAttributes[] {
    return this.commentsMap.get(taskView) ?? [];
  }

  static getSettingsArray(taskView: TaskView): SettingsAttributes[] {
    return this.settingsMap.get(taskView) ?? [];
  }
}
